import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { endOfMonth, format, subMonths } from 'date-fns'

import { Typography } from '@gto/ui/typography'

import type { AccountEarningsListPeriod } from '@/api/gen'
import { useAccountEarningsList } from '@/api/gen'
import { DataTable } from '@/components/data-table'
import { tableQueryState } from '@/components/table-query-state'
import { TABLE_SURFACE_BG } from '@/components/table-surface'
import { useAuth } from '@/hooks/use-auth'
import type { EarningsRow } from '@/pages/rewards/hooks/use-earnings-table-cols'
import { useEarningsTableCols } from '@/pages/rewards/hooks/use-earnings-table-cols'

import { SUMMARY_ROW_ID, summaryRow } from './earnings-summary-row'
import { EarningsTableFooter } from './earnings-table-footer'

export const EarningsTable = ({ period }: { period: AccountEarningsListPeriod }) => {
  const columns = useEarningsTableCols()
  const { isAuthorized, isInitialized } = useAuth()
  const { data, error, isError, isFetching, refetch } = useAccountEarningsList(
    { period },
    {
      query: {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
        placeholderData: keepPreviousData,
        enabled: isAuthorized,
      },
    },
  )

  const queryState = tableQueryState({
    error,
    hasData: data !== undefined,
    isError,
    isFetching,
    isInitialized,
    refetch,
  })
  const { loading } = queryState
  const deals = data ?? []
  const dealRows: EarningsRow[] = deals.map((deal, index) => ({ ...deal, id: String(index) }))
  // A REAL last row held in view by `sticky bottom-0` below, since the DS pins columns, not rows.
  // Gated on the FIRST load only: a refetch serves the previous deals, and stripping their total
  // while they are still on screen leaves a table whose rows no longer add up to anything.
  const rows = loading || deals.length === 0 ? dealRows : [...dealRows, summaryRow(deals)]

  return (
    <div>
      <Typography
        gutterBottom
        variant="h5"
      >
        <MonthLabel period={period} />
      </Typography>
      <DataTable
        columns={columns}
        data={rows}
        footer={loading ? undefined : <EarningsTableFooter />}
        getRowId={(row) => row.id}
        {...queryState}
        // `z-[4]` sits above the row-state layer (3) so deals scroll under the total, and below
        // the sticky header (5) so it never covers it. There is no free integer between them: 4 is
        // the DS's reserved `focus` layer (`PINNED_Z_INDEX`, exported from `@gto/ui/data-table`),
        // so a focused cell scrolling under the total ties and loses on DOM order. Acceptable —
        // the ring is occluded by a strip that is deliberately on top — but the real fix is a DS
        // token for a consumer's sticky body row.
        //
        // Reads the DATA's id: `row.id` is whatever `getRowId` returned, so keying off that would
        // break silently the day this table gains a composite id.
        getRowProps={(row) =>
          row.original.id === SUMMARY_ROW_ID
            ? { className: `sticky bottom-0 z-[4] ${TABLE_SURFACE_BG}` }
            : {}
        }
      />
    </div>
  )
}

const MonthLabel = ({ period }: { period: AccountEarningsListPeriod }) => {
  const date = format(new Date(), 'MMMM yyyy')
  const previousMonth = format(endOfMonth(subMonths(new Date(), 1)), 'MMMM yyyy')

  return (
    <FormattedMessage
      id={`rewards.earnings.table.${period}`}
      values={{
        month: period === 'this_month' ? date : previousMonth,
      }}
    />
  )
}
